import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { catchError, concatMap, debounceTime, first, of, Subject, Subscription } from 'rxjs';
import { CreditCardDirectivesModule } from 'angular-cc-library';
import { OpenPayForm } from '@getopenpay/openpay-js';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

import { PaymentsService } from 'app/shared/services/payments.service';
import { ProfileService } from 'app/shared/services/profile.service';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { TokensService } from '../../../shared/services/tokens.service';
import { ProfileStateService } from '../../../shared/services/profile-state.service';
import { OpenPayService } from 'app/shared/services/open-pay.service';

import { ButtonComponent } from 'app/shared/components/button/button.component';
import { PaymentFailedComponent } from '../payment-failed/payment-failed.component';
import { SkeletonLoaderComponent } from 'app/shared/components/skeleton-loader/skeleton-loader.component';

import {
  IOpenPayConfig,
  IPaymentErrorMessage,
  IPaymentResult,
  IPurchaseData,
  ISubscriptionData
} from 'app/interfaces/payments.interfaces';
import { IButtonConfig } from 'app/shared/interfaces/button.interfaces';
import { IBillingPostResponse } from 'app/interfaces/profile.interfaces';
import { ITokenPlan } from '../../../shared/interfaces/token.interfaces';

import {
  EPaymentErrorMessage,
  EPaymentErrorTitle,
  EPaymentField,
  EPaymentStatus,
  EPaymentType
} from 'app/enums/payments.enums';
import { EButtonTypes, EButtonSizes } from 'app/shared/enums/button.enums';
import { EProfileState } from 'app/enums/profile.enums';
import { ELocalStorageKey } from 'app/shared/enums/shared.enums';
import { EImageUsage } from 'app/shared/enums/image-usage.enums';

import { COUNTRIES, ICountry } from '../../../shared/const/countries';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'stxt-payment-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    CreditCardDirectivesModule,
    ButtonComponent,
    PaymentFailedComponent,
    SkeletonLoaderComponent
  ],
  templateUrl: './payment-form.component.html',
  styleUrl: './payment-form.component.scss'
})
export class PaymentFormComponent implements OnInit, OnDestroy {
  @Input() firstCheckOut: boolean;
  @Input() creator: string;
  @Input() creatorId: string;
  @Input() subscriptionData?: ISubscriptionData;
  @Input() purchaseData?: IPurchaseData;
  @Input() tokenData?: ITokenPlan;
  @Input() paymentType: string;
  @Input() oldPaymentMethodId?: string;
  @Input() wsConnectionId: string;
  @Input() editMode?: boolean = false;
  @Output() paymentSuccessEvent: EventEmitter<IPaymentResult> = new EventEmitter<IPaymentResult>();
  @Output() formValidityChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() paymentFinishEvent: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('myDrop') myDrop!: NgbDropdown;

  public addBtnConfig: IButtonConfig = {
    text: 'Add payment method',
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Default
  };
  public availablePayments: string[] = ['Visa', 'Mastercard'];
  public paymentsSubscription: Subscription;
  public formValiditySubscription: Subscription;
  public selectedCountry?: ICountry;
  public checkoutSecureToken: string;
  public EPaymentType = EPaymentType;
  public user = JSON.parse(localStorage.getItem('user'));
  public isError: boolean = false;
  public showError: boolean = false;
  public errorMessages: IPaymentErrorMessage;
  public cardFieldErrors: { [key: string]: boolean } = {
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
    firstName: false,
    lastName: false,
    zipCode: false,
    country: false
  };
  private readonly searchTermSubject = new Subject<string>();
  protected readonly EImageUsage = EImageUsage;
  private searchTerm: string = '';

  openPayForm: OpenPayForm;
  isStagingEnv: boolean;
  countries = COUNTRIES;
  fieldErrorStates: { [key: string]: boolean } = {};
  userId = localStorage.getItem(ELocalStorageKey.UserId);
  highlightedCountryIndex: number | null = null;
  openpayLoaded: boolean = false;

  constructor(
    private readonly paymentsService: PaymentsService,
    private readonly profileService: ProfileService,
    private readonly analyticsService: AnalyticsService,
    private readonly tokensService: TokensService,
    private readonly profileStateService: ProfileStateService,
    private readonly openPayService: OpenPayService
  ) {}

  ngOnInit(): void {
    this.resetErrorStates();
    if (
      environment.serviceEnvironment === 'staging' ||
      environment.serviceEnvironment === 'development'
    ) {
      this.isStagingEnv = true;
    }
    this.subscribeOnPaymentsEvent();
    this.getSecureToken();
    this.handleCountrySearch();
    this.addBtnConfig.text =
      this.paymentType === EPaymentType.EditBilling ? 'Save changes' : 'Add payment method';
  }

  getSecureToken(): void {
    this.profileService
      .generateSecureToken()
      .pipe(first())
      .subscribe({
        next: (res: IBillingPostResponse) => {
          this.checkoutSecureToken = res.secure_token;
          this.initOpenpayForm();
        },
        error: (err: Error) => {
          console.log(err);
        }
      });
  }

  async initOpenpayForm(): Promise<void> {
    this.resetErrorStates();

    const config: IOpenPayConfig = {
      baseUrl: this.isStagingEnv ? 'https://cde.openpaystaging.com' : undefined,
      checkoutSecureToken: this.checkoutSecureToken,
      formTarget: '#payment-form',
      onValidationError: () => {},
      onLoad: () => (this.openpayLoaded = true),
      onSetupPaymentMethodSuccess: (paymentMethodId: string) =>
        this.onAddBillingInfoSuccess(paymentMethodId),
      onCheckoutError: () => this.onCheckoutError(),
      onChange: (elementId: string, field: string, errors: string[]) =>
        this.onChange(field, errors),
      onBlur: (elementId: string, field: string) => this.onBlur(elementId, field)
    };

    try {
      this.openPayForm = await this.openPayService.createInstance(config);

      const styles = {
        color: '#DDC9DE',
        fontSize: '14px',
        fontFamily: 'sans-serif',
        fontWeight: '400',
        backgroundColor: 'transparent',
        borderRadius: '12px',
        padding: '10px 16px',
        placeholderStyle: {
          color: '#8c768e',
          fontSize: '14px',
          fontWeight: '400',
          fontFamily: 'sans-serif',
          letterSpacing: '-0.28px',
          lineHeight: '20px'
        }
      };

      this.openPayForm
        .createElement('card-number', {
          styles: { ...styles, placeholder: 'Credit card' }
        })
        .mount('#card-number-element');

      this.openPayForm
        .createElement('card-expiry', {
          styles: { ...styles, placeholder: 'Expiry Date (MM/YY)' }
        })
        .mount('#card-expiry-element');

      this.openPayForm
        .createElement('card-cvc', {
          styles: { ...styles, placeholder: 'Security code' }
        })
        .mount('#card-cvc-element');

      this.isError = true;
    } catch (error) {
      console.error('Failed to initialize OpenPay form:', error);
      this.onCheckoutError();
    }
  }

  onCheckoutError(): void {
    this.isError = true;
    this.showError = true;
    this.errorMessages = {
      errorType: EPaymentField.generic,
      errorTitle: EPaymentErrorTitle.generic,
      errorMessage: EPaymentErrorMessage.generic
    };
    this.paymentFinishEvent.emit();
  }

  setValue(value: ICountry): void {
    this.selectedCountry = value;
    this.cardFieldErrors['country'] = false;
  }

  onBlur(elementId: string, field: string): void {
    if (Object.keys(this.cardFieldErrors).includes(field)) {
      this.cardFieldErrors[field] = this.fieldErrorStates[field] || false;
    }
    this.isError = Object.values(this.fieldErrorStates).includes(true);
    this.formValidityChange.emit(this.isError);
  }

  onChange(field: string, errors: string[]): void {
    this.fieldErrorStates[field] = errors.length > 0;
    if (Object.keys(this.cardFieldErrors).includes(field)) {
      this.cardFieldErrors[field] = errors.length > 0;
    }
  }

  addBillingInfo(): void {
    if (this.checkErrors() || this.addBtnConfig.isLoading) return;
    this.openPayForm.submit();

    if (this.paymentType !== EPaymentType.EditBilling && this.paymentType !== EPaymentType.Billing)
      return;
    this.addBtnConfig.isLoading = true;
  }

  onAddBillingInfoSuccess(paymentMethodId: string): void {
    //edit mode triggered only in checkout component to process purchase, in billing info used separated type edit billing
    this.editMode
      ? this.editBillingPayment(paymentMethodId)
      : this.paymentTypeCheck(paymentMethodId);
  }

  markErrorFields(): void {
    if (this.isError) {
      for (const field of Object.keys(this.cardFieldErrors)) {
        this.cardFieldErrors[field] = true;
      }
    }
  }

  private setFieldValidationState(field: string, isValid: boolean): boolean {
    const hasError = !isValid;
    this.cardFieldErrors[field] = hasError;
    this.fieldErrorStates[field] = hasError;
    return hasError;
  }

  private validateCreditCardFields(): boolean {
    const openPayFields = ['cardNumber', 'cardExpiry', 'cardCvc'];
    let hasErrors = false;

    for (const field of openPayFields) {
      const fieldNotTouched = !Object.prototype.hasOwnProperty.call(this.fieldErrorStates, field);
      const fieldHasErrors = this.fieldErrorStates[field] === true;
      if (this.setFieldValidationState(field, !(fieldNotTouched || fieldHasErrors))) {
        hasErrors = true;
      }
    }

    return hasErrors;
  }

  private validateRequiredTextFields(): boolean {
    const requiredFields = ['firstName', 'lastName', 'zipCode'];
    let hasErrors = false;

    for (const field of requiredFields) {
      const value = (
        document.querySelector(`[data-opid="${field}"]`) as HTMLInputElement
      )?.value?.trim();
      if (this.setFieldValidationState(field, !!value)) {
        hasErrors = true;
      }
    }

    return hasErrors;
  }

  private validateCountry(): boolean {
    return this.setFieldValidationState('country', !!this.selectedCountry);
  }

  checkErrors(): boolean {
    const hasCreditCardErrors = this.validateCreditCardFields();
    const hasTextFieldErrors = this.validateRequiredTextFields();
    const hasCountryError = this.validateCountry();

    const hasErrors = hasCreditCardErrors || hasTextFieldErrors || hasCountryError;

    // Update overall form error state
    this.isError = hasErrors;
    this.formValidityChange.emit(this.isError);

    return hasErrors;
  }

  handleCountrySearch(): void {
    this.searchTermSubject.pipe(debounceTime(1500)).subscribe(() => {
      this.searchTerm = '';
    });
  }

  handleKeyboardNavigation(event: KeyboardEvent): void {
    if (!/^[a-zA-Z]$/.test(event.key)) return;

    event.preventDefault();
    !this.myDrop.isOpen() && this.myDrop.open();

    this.searchTerm += event.key.toLowerCase();
    const countryIndex = this.countries.findIndex(country =>
      country.name.toLowerCase().startsWith(this.searchTerm)
    );

    if (countryIndex >= 0) {
      this.highlightedCountryIndex = countryIndex;
      document.getElementById(`country-${countryIndex}`)?.scrollIntoView({ block: 'center' });
      this.setValue(this.countries[countryIndex]);
    }

    this.searchTermSubject.next(this.searchTerm);
  }

  validateField(field: string, value: string): void {
    const trimmedValue = value?.trim();
    const hasError =
      ['firstName', 'lastName', 'zipCode', 'country'].includes(field) && !trimmedValue;

    this.cardFieldErrors[field] = hasError;
    this.fieldErrorStates[field] = hasError;
    this.isError = Object.values(this.fieldErrorStates).includes(true);
    this.formValidityChange.emit(this.isError);
  }

  subscribeOnPaymentsEvent(): void {
    this.paymentsSubscription = this.paymentsService.$submitPayment.subscribe({
      next: value => {
        if (!value) return;
        this.addBillingInfo();
      }
    });
  }

  paymentTypeCheck(id: string): void {
    switch (this.paymentType) {
      case EPaymentType.Subscription:
        this.subscriptionPayment(id);
        break;
      case EPaymentType.Purchase:
        this.purchasePayment(id);
        break;
      case EPaymentType.Token:
        this.tokenPurchase(id);
        break;
      case EPaymentType.EditBilling:
        this.editBillingPayment(id);
        break;
      case EPaymentType.Billing:
        this.paymentSuccessEvent.emit({
          status: EPaymentStatus.Success,
          paymentType: EPaymentType.Billing
        });
        this.analyticsService.onboardingProfile(EProfileState.PROFILE_BILLING_INFO, this.userId);
        break;
    }
  }

  editBillingPayment(id: string): void {
    const payload = {
      old_payment_method_id: this.oldPaymentMethodId,
      new_payment_method_id: id
    };
    this.profileService
      .editBillingInfo(payload)
      .pipe(first())
      .subscribe({
        next: res => {
          if (!res) return;
          this.profileStateService.refreshBillingInfo();
          this.addBtnConfig.isLoading = false;
          ///process following payment type check or init result event
          this.editMode
            ? this.paymentTypeCheck(id)
            : this.initResultEvent(EPaymentStatus.Success, EPaymentType.Billing);
        },
        error: err => {
          if (err.status == 200) {
            ///process following payment type check or init result event
            this.editMode
              ? this.paymentTypeCheck(id)
              : this.initResultEvent(EPaymentStatus.Success, EPaymentType.Billing);
          } else {
            this.addBtnConfig.isLoading = false;
            this.initResultEvent(
              EPaymentStatus.Error,
              EPaymentType.EditBilling,
              err.error.error_message
            );
          }
        }
      });
  }

  tokenPurchase(id: string): void {
    this.tokensService
      .purchaseTokens({
        billing_token: id,
        tokens_pack_id: this.tokenData.id,
        quantity: 1
      })
      .pipe(
        first(),
        concatMap(() =>
          this.tokensService.fetchTokensBalance(true).pipe(
            catchError(() => {
              return of(null);
            })
          )
        )
      )
      .subscribe({
        next: () => {
          this.initResultEvent(EPaymentStatus.Success, EPaymentType.Token);
          this.tokensService.successTokenMediaPurchaseEvent$.next(true);
        },
        error: err => {
          if (err.status === 200) {
            this.tokensService.fetchTokensBalance(true).subscribe(() => {
              this.initResultEvent(EPaymentStatus.Success, EPaymentType.Token);
              this.tokensService.successTokenMediaPurchaseEvent$.next(true);
            });
          } else {
            this.markErrorFields();
            this.initResultEvent(EPaymentStatus.Error, EPaymentType.Token, err.error.error_message);
          }
        }
      });
  }

  subscriptionPayment(id: string): void {
    this.paymentsService
      .creatorSubscriptionRequest({
        creator_id: this.creatorId,
        billing_token: id,
        plan_code: this.subscriptionData.code,
        ws_conn_id: this.wsConnectionId
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.initResultEvent(EPaymentStatus.Success, EPaymentType.Subscription);
          this.profileStateService.refreshBillingInfo();
        },
        error: err => {
          if (err.status === 200) {
            this.initResultEvent(EPaymentStatus.Success, EPaymentType.Subscription);
            this.profileStateService.refreshBillingInfo();
          } else {
            this.markErrorFields();
            this.initResultEvent(
              EPaymentStatus.Error,
              EPaymentType.Subscription,
              err.error.error_message
            );
          }
          this.addBtnConfig.isLoading = false;
        }
      });
  }

  purchasePayment(id: string): void {
    this.paymentsService
      .purchaseMediaRequest({
        creator_id: this.creatorId,
        billing_token: id,
        message_timestamp: Number(this.purchaseData.message_timestamp),
        ws_conn_id: this.wsConnectionId
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.initResultEvent(EPaymentStatus.Success, EPaymentType.Purchase);
        },
        error: err => {
          if (err.status === 200) {
            this.initResultEvent(EPaymentStatus.Success, EPaymentType.Purchase);
          } else {
            this.markErrorFields();
            this.initResultEvent(
              EPaymentStatus.Error,
              EPaymentType.Purchase,
              err.error.error_message
            );
          }
        }
      });
  }

  initResultEvent(
    status: EPaymentStatus,
    paymentType: EPaymentType | string,
    error?: string
  ): void {
    const eventPayload: IPaymentResult = {
      status: status,
      paymentType: paymentType
    };

    if (error) {
      eventPayload.error_message = error;
    }

    this.paymentSuccessEvent.emit(eventPayload);
  }

  toUpperCase($event: Event) {
    const input = $event.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
  }

  private resetErrorStates(): void {
    this.isError = false;
    this.showError = false;
    this.fieldErrorStates = {};
    this.cardFieldErrors = {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
      firstName: false,
      lastName: false,
      zipCode: false,
      country: false
    };
  }

  ngOnDestroy(): void {
    this.openPayService.destroyInstance(); // Synchronous, fire-and-forget
    this.openPayForm = null;
    this.paymentsSubscription?.unsubscribe();
    this.formValiditySubscription?.unsubscribe();
    this.searchTermSubject.complete();
  }
}
